import React, { createContext, useContext, useEffect, useState } from 'react';
import { Theme, ThemeMode } from '../types/theme';
import { whitelabelConfig } from '../config/whitelabel.config';
import { getTheme } from '../themes/theme';
import { applyThemeVariables, getInitialThemeMode, watchSystemTheme } from '../utils/theme';
import { useBranding } from './BrandingContext';

interface ThemeContextValue {
  theme: Theme;
  toggleTheme: () => void;
  setThemeMode: (mode: ThemeMode) => void;
  isDark: boolean;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultMode?: ThemeMode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultMode,
}) => {
  const { branding } = useBranding();
  const [mode, setMode] = useState<ThemeMode>(() => defaultMode || getInitialThemeMode());
  const [theme, setTheme] = useState<Theme>(() => getTheme(mode));
  const [isThemeApplied, setIsThemeApplied] = useState(false);

  const updateTheme = (newMode: ThemeMode) => {
    const newTheme = getTheme(newMode);
    setTheme(newTheme);
    setMode(newMode);
    localStorage.setItem('theme', newMode);
    
    // Ensure theme variables are applied after state updates
    requestAnimationFrame(() => {
      applyThemeVariables(newTheme);
      setIsThemeApplied(true);
    });
  };

  // Initialize theme
  useEffect(() => {
    updateTheme(mode);
  }, []);

  // Watch for system theme changes
  useEffect(() => {
    const cleanup = watchSystemTheme((isDark) => {
      const preferredMode = isDark ? 'dark' : 'light';
      // Only update if user hasn't manually set a preference
      if (!localStorage.getItem('theme')) {
        updateTheme(preferredMode);
      }
    });

    return cleanup;
  }, []);
  
  // Update theme when branding changes
  useEffect(() => {
    updateTheme(mode);
  }, [branding]);

  // Ensure theme is properly applied after any theme changes
  useEffect(() => {
    if (theme && !isThemeApplied) {
      requestAnimationFrame(() => {
        applyThemeVariables(theme);
        setIsThemeApplied(true);
      });
    }
  }, [theme, isThemeApplied]);

  const toggleTheme = () => {
    setIsThemeApplied(false);
    updateTheme(mode === 'light' ? 'dark' : 'light');
  };

  const setThemeMode = (newMode: ThemeMode) => {
    setIsThemeApplied(false);
    updateTheme(newMode);
  };

  const value = {
    theme,
    toggleTheme,
    setThemeMode,
    isDark: mode === 'dark',
  };

  if (!isThemeApplied) {
    return null; // Don't render children until theme is properly applied
  }

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
